import { capitalCase } from "change-case";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const OBJECT_KEYS_TO_IGNORE = ["id", "createdAt", "individuals"];
const OBJECT_KEYS_TO_ADD = ["addressLine2", "addressLine3", "unitNumber"];

interface Application {
  applicationId: string;
  status: string;
  customerDetailsCorporation?: any;
  customerDetailsIndividual?: any;
}

function flattenObject(
  obj: any,
  prefix = "",
  keysToIgnore: string[] = [],
  keysToAdd: string[] = [],
): Record<string, any> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const newKey = prefix ? `${prefix}_${key}` : key;

    if (
      keysToIgnore.includes(key) ||
      (value === null && !keysToAdd.includes(key)) ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return acc;
    }

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      Object.assign(acc, flattenObject(value, newKey, keysToIgnore, keysToAdd));
    } else if (Array.isArray(value)) {
      acc[capitalCase(newKey)] = value.join(", ");
    } else {
      acc[capitalCase(newKey)] = value;
    }

    return acc;
  }, {});
}

function processApplications(
  applications: Application[],
  type: "corporate" | "individual",
): Record<string, any>[] {
  return applications
    .map((application) => {
      const details =
        type === "corporate"
          ? application.customerDetailsCorporation
          : application.customerDetailsIndividual;
      if (details) {
        const flattenedDetails = flattenObject(
          details,
          "",
          OBJECT_KEYS_TO_IGNORE,
          OBJECT_KEYS_TO_ADD,
        );
        return {
          "Application ID": application.applicationId,
          Status: application.status,
          ...flattenedDetails,
        };
      }
      return null;
    })
    .filter(Boolean) as Record<string, any>[];
}

export function generateApplicationsXlsx(
  applications: Application[],
  name = "izziwallet",
  t: (key: string) => string,
) {
  const corporateApplications = processApplications(applications, "corporate");
  const individualApplications = processApplications(
    applications,
    "individual",
  );

  const corporateIndividuals = applications.flatMap((application) => {
    const { customerDetailsCorporation } = application;
    if (customerDetailsCorporation?.individuals) {
      return customerDetailsCorporation.individuals.map((individual) => {
        const flattenedDetails = flattenObject(
          individual,
          "",
          OBJECT_KEYS_TO_IGNORE,
        );
        return {
          "Application ID": application.applicationId,
          "Entity Reference": customerDetailsCorporation.registeredName,
          ...flattenedDetails,
        };
      });
    }
    return [];
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(corporateApplications),
    "CORPORATE Applications",
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(corporateIndividuals),
    "CORPORATE Individuals",
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(individualApplications),
    "INDIVIDUAL Applications",
  );

  const wbBlob = new Blob(
    [XLSX.write(wb, { bookType: "xlsx", type: "array" })],
    {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  );

  saveAs(wbBlob, `${name}_exported_applications.xlsx`);
}
